import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  applyTypography,
  BaseButton,
  useMediaQuery,
  useAuth,
  useLoginRequiredModal,
  trackWorkshopBookStart,
  useT,
} from '@repo/common';
import { ReactComponent as CrownIcon } from '@repo/common/assets/icons/16px/icon_16px_crown.svg';
import { ReactComponent as HeartFilledIcon } from '@repo/common/assets/icons/32px/icon_32px_heart_filled.svg';
import { ReactComponent as HeartOutlineIcon } from '@repo/common/assets/icons/32px/icon_32px_heart_outline.svg';
import { ReactComponent as ShoppingBagIcon } from '@repo/common/assets/icons/32px/icon_32px_shopping-bag_outline_add.svg';
import DateSelectionModal from '../../Kpathport/DateSelectionModal';
import KeywordPillList from '../../Kpathport/PathFeed/KeywordPillList';
import {
  getWorkshopDetail,
  getWorkshopList,
} from '../../../api/workshop/workshop.api';
import {
  addWorkshopLike,
  getWorkshopLikeStatus,
  removeWorkshopLike,
} from '../../../api/workshop/workshopLike.api';
import { transformDateSlots } from '../../Kpathport/utils/dateSlotUtils';
import { HOME_V2_IMAGES } from './homeV2Assets';

const CARD_IMAGE = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-cardimg.svg`;

const SECTION_WORKSHOP_COUNT = {
  mobileMini: 6,
  mobile: 6,
  tabletH: 6,
  tabletV: 6,
  desktopL: 8,
  desktopXXL: 8,
};
const SECTION_MAX_WORKSHOP_COUNT = Math.max(
  ...Object.values(SECTION_WORKSHOP_COUNT),
);
const SECTION_BREAKPOINTS = {
  mobile: 414,
  tabletH: 768,
  tabletV: 1180,
  desktopL: 1440,
  desktopXXL: 1920,
};
const SECTION_TITLE = 'Free Booster Lectures';
const SECTION_TITLE_TYPOGRAPHY_CLASS =
  'text-title2 tabletH:text-title2 tabletV:text-h7 desktopL:text-h6 desktopXXL:text-h4';
const SECTION_TITLE_MOBILE = 'Free Lectures below';

const getWorkshopItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const normalizeWorkshops = (response = []) =>
  getWorkshopItems(response)
    .slice(0, SECTION_MAX_WORKSHOP_COUNT)
    .map((workshop) => {
      const tags = [];
      if (workshop.hit) tags.push({ label: 'Hit', tone: 'accent' });
      if (workshop.free) tags.push({ label: 'Free', tone: 'neutral' });

      return {
        id: workshop.id,
        title: workshop.title || '',
        description: workshop.description || '',
        image: workshop.thumbnailImage || CARD_IMAGE,
        tags,
        keywords: workshop.keywords || [],
        stepNumber: workshop.category,
        stepName: workshop.category,
      };
    });

const Tag = ({ label, tone = 'neutral' }) => {
  const t = useT();
  const isHit = label?.toLowerCase() === 'hit';
  const toneClass =
    tone === 'accent'
      ? 'border-orange-orange03 bg-orange-orange02 text-orange-orange04'
      : 'border-grayscale-gray00 bg-grayscale-gray01 text-icon-primary';

  return (
    <span
      className={twMerge(
        'inline-flex h-[30px] min-w-[50px] items-center justify-center rounded-full border px-2.5 py-1.5 desktopL:h-[34px] desktopL:min-w-[60px] desktopL:px-3 desktopL:py-2',
        'shadow-[0_3px_2px_rgba(23,20,16,0.08),0_4px_6px_rgba(23,20,16,0.16)]',
        applyTypography('b12Strong', label),
        toneClass,
        isHit && 'gap-1',
      )}
    >
      {isHit && (
        <CrownIcon
          aria-hidden='true'
          className='size-4 [&_path]:fill-current'
        />
      )}
      {t(label)}
    </span>
  );
};

// 카드 너비를 넘는 칩은 KeywordPillList 가 개수를 줄이고 "…" 칩을 붙인다
const KeywordPill = (label, key, title) => (
  <span
    key={key}
    title={title || label}
    className={twMerge(
      'inline-flex h-[26px] min-w-0 max-w-full shrink-0 items-center justify-center overflow-hidden rounded-[99px] bg-grayscale-gray02 px-2.5 py-1 tabletH:rounded-md tabletH:px-2 desktopL:h-7 desktopL:rounded-[99px] desktopL:px-3 desktopL:py-[6px]',
    )}
  >
    <span className='truncate text-[12px] font-medium leading-[1.4] text-grayscale-gray05 desktopL:text-[13px]'>
      {label}
    </span>
  </span>
);

const WorkshopCard = ({ workshop, isWishlisted, onToggleWishlist }) => {
  const t = useT();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const HeartIcon = isWishlisted ? HeartFilledIcon : HeartOutlineIcon;
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateSlots, setDateSlots] = useState([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const { showLoginModal } = useLoginRequiredModal();
  const fetchSeqRef = useRef(0);

  const handleCardClick = () => {
    if (isDateModalOpen) return;
    if (workshop.id) {
      navigate(`/community/kpathport/workshop/${workshop.id}`);
    }
  };

  const handleWishlistToggle = (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      showLoginModal('home_free_workshop_cta');
      return;
    }
    onToggleWishlist?.(workshop.id, !isWishlisted);
  };

  const handleQuickAddClick = (event) => {
    event.stopPropagation();

    if (!isLoggedIn) {
      showLoginModal('home_free_workshop_cta');
      return;
    }

    trackBookingStart();
    setIsDateModalOpen(true);
  };

  const handleMobileBagClick = (event) => {
    event.stopPropagation();

    if (!isLoggedIn) {
      showLoginModal('home_free_workshop_cta');
      return;
    }

    trackBookingStart();
    setIsDateModalOpen(true);
  };

  useEffect(() => {
    if (!isDateModalOpen || !workshop.id) return;

    const seq = ++fetchSeqRef.current;

    getWorkshopDetail(parseInt(workshop.id, 10))
      .then((data) => {
        if (fetchSeqRef.current !== seq) return;
        setDateSlots(transformDateSlots(data?.dateSlots));
        setIsBlocked(data?.isBlocked);
      })
      .catch((error) => {
        handleQuickAddClick;
        console.error('Home free booster dateSlots fetch failed:', error);
        if (fetchSeqRef.current !== seq) return;
        setDateSlots([]);
        setIsBlocked(false);
      });
  }, [isDateModalOpen, workshop.id]);

  const trackBookingStart = () => {
    trackWorkshopBookStart({
      workshop_id: workshop.id,
      workshop_name: workshop.title,
      step_number: workshop.stepNumber,
      step_name: workshop.stepName,
      source: 'landing_booster',
    });
  };

  return (
    <article
      className='flex flex-col items-start w-full min-w-0 overflow-hidden cursor-pointer group'
      onClick={handleCardClick}
    >
      <div className='relative flex h-[240px] w-full flex-col items-start justify-between overflow-hidden rounded-[20px] bg-grayscale-gray01 p-4 tabletH:aspect-[340/252] tabletH:h-auto tabletH:p-6 desktopL:aspect-auto desktopL:h-[252px] desktopL:rounded-[30px] desktopL:px-5 desktopL:py-4'>
        <img
          src={workshop.image}
          alt={workshop.title}
          className='absolute inset-0 size-full rounded-[inherit] object-cover'
        />
        <div className='absolute inset-0 rounded-[inherit] bg-gradient-to-b from-transparent from-50% to-[rgba(0,0,0,0.5)] opacity-0 transition-opacity duration-300 desktopL:opacity-100' />

        <div className='relative z-[1] flex w-full items-center gap-2'>
          <div className='flex items-start flex-1 min-w-0 gap-1.5 desktopL:gap-2'>
            {workshop.tags.map((tag) => (
              <Tag key={`${workshop.id}-${tag.label}`} {...tag} />
            ))}
          </div>
          <BaseButton
            type='text'
            size='s'
            aria-label={
              isWishlisted ? 'remove from wishlist' : 'add to wishlist'
            }
            aria-pressed={isWishlisted}
            onClick={handleWishlistToggle}
            className='size-8 min-w-0 shrink-0 overflow-hidden rounded-full border-0 bg-transparent p-0 hover:bg-transparent drop-shadow-[0_0_3px_rgba(104,101,97,0.08),0_0_8px_rgba(104,101,97,0.14)]'
            icon={
              <HeartIcon
                aria-hidden='true'
                className={twMerge(
                  'size-7 [&_path]:stroke-current',
                  isWishlisted
                    ? 'text-icon-failed [&_path]:fill-current'
                    : 'text-icon-inverse [&_path]:fill-transparent',
                )}
              />
            }
          />
        </div>

        <div className='relative z-[1] flex h-12 w-full items-start'>
          <BaseButton
            type='primary'
            size='l'
            hideIcon
            typo={false}
            onClick={handleQuickAddClick}
            className={twMerge(
              'hidden h-12 w-full min-w-0 items-center justify-center rounded-[99px] border-0 bg-green-deep02 px-4 py-3 text-[16px] font-medium leading-[1.5] text-text-inverse opacity-0 shadow-[0_3px_2px_rgba(23,20,16,0.08),0_4px_6px_rgba(23,20,16,0.16)] transition-[background-color,opacity] duration-300 hover:bg-green-deep03 desktopL:flex desktopL:group-hover:opacity-100',
            )}
          >
            {t('Quick add')}
          </BaseButton>
          <BaseButton
            type='primary'
            size='l'
            onClick={handleMobileBagClick}
            aria-label='Open date selection'
            className='absolute bottom-0 right-0 z-[1] size-12 min-w-0 rounded-full border-0 bg-green-deep02 p-0 pb-[3px] shadow-[0_3px_2px_rgba(23,20,16,0.08),0_4px_6px_rgba(23,20,16,0.16)] desktopL:hidden'
            icon={
              <ShoppingBagIcon
                aria-hidden='true'
                className='size-8 text-icon-inverse [&_path]:stroke-current'
              />
            }
          />
        </div>
      </div>

      <div className='flex w-full flex-col items-start px-2.5 pb-10 pt-2 tabletH:pt-3 tabletH:pb-9 desktopL:px-0 desktopL:pb-[60px]'>
        <div className='flex items-center justify-center w-full pb-1'>
          <p
            className={twMerge(
              'line-clamp-2 min-h-12 min-w-0 flex-1 text-[20px] font-medium leading-[1.2] text-text-primary tabletH:min-h-0 tabletH:text-[18px] tabletH:leading-[1.5] desktopL:text-[24px] desktopL:leading-[1.2]',
            )}
          >
            {workshop.title}
          </p>
        </div>
        <div className='flex items-center justify-center w-full pb-2 desktopL:pb-3'>
          <p
            className={twMerge(
              'line-clamp-1 min-w-0 flex-1 text-[12px] font-normal leading-[1.4] text-text-placeholder tabletH:h-5 tabletH:max-h-5 tabletH:text-[14px]',
            )}
          >
            {workshop.description}
          </p>
        </div>
        <KeywordPillList
          items={workshop.keywords}
          renderPill={KeywordPill}
          className='flex h-[26px] w-full min-w-0 items-start gap-2 overflow-hidden desktopL:h-7'
        />
      </div>

      <DateSelectionModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        dateSlots={dateSlots}
        isBlocked={isBlocked}
      />
    </article>
  );
};

const FreeBoosterLectures = ({ isBusiness = false }) => {
  const t = useT();
  const { isLoggedIn } = useAuth();
  const isMobile = useMediaQuery(
    `(min-width: ${SECTION_BREAKPOINTS.mobile}px)`,
  );
  const isTabletH = useMediaQuery(
    `(min-width: ${SECTION_BREAKPOINTS.tabletH}px)`,
  );
  const isTabletV = useMediaQuery(
    `(min-width: ${SECTION_BREAKPOINTS.tabletV}px)`,
  );
  const isDesktopL = useMediaQuery(
    `(min-width: ${SECTION_BREAKPOINTS.desktopL}px)`,
  );
  const isDesktopXXL = useMediaQuery(
    `(min-width: ${SECTION_BREAKPOINTS.desktopXXL}px)`,
  );
  const [workshops, setWorkshops] = useState([]);
  const [wishlistMap, setWishlistMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const workshopCount = isDesktopXXL
    ? SECTION_WORKSHOP_COUNT.desktopXXL
    : isDesktopL
      ? SECTION_WORKSHOP_COUNT.desktopL
      : isTabletV
        ? SECTION_WORKSHOP_COUNT.tabletV
        : isTabletH
          ? SECTION_WORKSHOP_COUNT.tabletH
          : isMobile
            ? SECTION_WORKSHOP_COUNT.mobile
            : SECTION_WORKSHOP_COUNT.mobileMini;
  const displayedWorkshops = workshops.slice(0, workshopCount);

  useEffect(() => {
    let cancelled = false;

    const fetchWorkshops = async () => {
      try {
        setIsLoading(true);
        const workshopList = await getWorkshopList();
        const visibleWorkshops = normalizeWorkshops(workshopList);

        if (cancelled) return;
        setWorkshops(visibleWorkshops);

        if (!isLoggedIn || visibleWorkshops.length === 0) {
          setWishlistMap({});
          return;
        }

        const likeEntries = await Promise.all(
          visibleWorkshops.map(async (workshop) => {
            try {
              const liked = await getWorkshopLikeStatus(workshop.id);
              return [workshop.id, !!liked];
            } catch {
              return [workshop.id, false];
            }
          }),
        );

        if (cancelled) return;
        setWishlistMap(Object.fromEntries(likeEntries));
      } catch (error) {
        console.error('Home free booster workshops fetch failed:', error);
        if (!cancelled) {
          setWorkshops([]);
          setWishlistMap({});
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchWorkshops();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const handleToggleWishlist = async (workshopId, nextWishlisted) => {
    setWishlistMap((prev) => ({
      ...prev,
      [workshopId]: nextWishlisted,
    }));

    try {
      if (nextWishlisted) {
        await addWorkshopLike(workshopId);
      } else {
        await removeWorkshopLike(workshopId);
      }
    } catch (error) {
      console.error('Home free booster like toggle failed:', error);
      setWishlistMap((prev) => ({
        ...prev,
        [workshopId]: !nextWishlisted,
      }));
    }
  };

  return (
    <section className='px-4 pt-5 pb-40 bg-bg-twodays tabletH:px-6 tabletH:py-20 tabletV:px-10 desktopXXL:py-40'>
      <div className='mx-auto flex w-full max-w-[328px] flex-col items-center min-[414px]:max-w-[382px] tabletH:max-w-[720px] tabletV:max-w-[1100px] desktopL:max-w-[1062px] desktopXXL:max-w-[1462px]'>
        <div className='relative flex w-full items-center justify-center gap-[10px] pb-[24px] tabletH:pb-[40px] desktopXXL:pb-[60px]'>
          {!isBusiness && (
            <img
              src={HOME_V2_IMAGES.emphasisText}
              alt=''
              aria-hidden
              className='pointer-events-none absolute
              left-[calc(50%_-_65px)] top-[calc(50%_-_20px)]
              mobile:left-[calc(50%_-_65px)] mobile:top-[calc(50%_-_20px)]
              tabletH:left-[calc(50%_-_100px)] tabletH:top-[calc(50%_-_25px)]
              tabletV:left-[calc(50%_-_120px)] tabletV:top-[calc(50%_-_30px)]
              desktopL:left-[calc(50%_-_127px)] desktopL:top-[calc(50%_-_30px)]
              desktopXXL:left-[calc(50%_-_170.18px)] desktopXXL:top-[calc(50%_-_49px)]
              h-[51px] w-[86px] tabletV:h-[64px] tabletV:w-[108px] desktopXXL:h-[92px] desktopXXL:w-[153.635px]
              -translate-x-1/2 -translate-y-1/2 select-none'
            />
          )}
          <h2
            className={twMerge(
              SECTION_TITLE_TYPOGRAPHY_CLASS,
              'relative min-w-0 flex-1 text-center text-text-primary',
            )}
          >
            {isBusiness ? (
              '코레디가 글로벌 인재를 만나는 현장'
            ) : (
              <>
                <span className='tabletH:hidden'>{t(SECTION_TITLE_MOBILE)}</span>
                <span className='hidden tabletH:inline'>{t(SECTION_TITLE)}</span>
              </>
            )}
          </h2>
        </div>

        {isLoading ? (
          <div className='grid w-full grid-cols-1 gap-x-4 gap-y-0 tabletH:grid-cols-2 tabletV:grid-cols-3 tabletV:gap-x-[18px] desktopL:grid-cols-4 desktopL:gap-x-6'>
            {Array.from({ length: workshopCount }, (_, index) => (
              <div
                key={index}
                className='h-96 animate-pulse rounded-[20px] bg-grayscale-gray02 desktopL:rounded-[30px]'
              />
            ))}
          </div>
        ) : (
          <div className='grid w-full grid-cols-1 gap-x-4 gap-y-0 tabletH:grid-cols-2 tabletV:grid-cols-3 tabletV:gap-x-[18px] desktopL:grid-cols-4 desktopL:gap-x-6'>
            {displayedWorkshops.map((workshop) => (
              <WorkshopCard
                key={workshop.id}
                workshop={workshop}
                isWishlisted={!!wishlistMap[workshop.id]}
                onToggleWishlist={handleToggleWishlist}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FreeBoosterLectures;
